import {
	ProductKeys,
	type ProductKeys1P,
	type ProductKeys3P,
} from '../../../../../common/constants/products';
import { LastModified } from '../../../../../common/constants/schemas/query-params';
import {
	getInvalidDateRange,
	getProducts3PFromConfigForFilter,
} from '../../../../../common/utils/filters';
import {
	type FetchedSelectFilterOption,
	type SelectFilterMonad,
	type SelectFilterOption,
} from '../../../../filters/base-select-filter/types';
import { createSingleWithCustomOptionSelectFilter } from '../../../../filters/single-select-with-custom-option-filter';
import type { CloudConfig } from '../../../../filters/types';

import { lastUpdatedLabels } from './messages';

const LAST_UPDATED_VALUES = [
	'today',
	'yesterday',
	'past7Days',
	'past30Days',
	'pastYear',
	'custom',
] as const;

type LastUpdatedType = (typeof LAST_UPDATED_VALUES)[number];

const sortCompareFn = (a: FetchedSelectFilterOption, b: FetchedSelectFilterOption) => {
	return (
		LAST_UPDATED_VALUES.indexOf(a.value as LastUpdatedType) -
		LAST_UPDATED_VALUES.indexOf(b.value as LastUpdatedType)
	);
};
const isIsoDate = (str?: string) => {
	if (!str) {
		return false;
	}
	if (!/\d{4}-\d{2}-\d{2}/.test(str)) {
		return false;
	}
	const d = new Date(str);
	return !isNaN(d.getTime());
};

const getLastUpdatedValue = () => {
	// fg('rovo-custom-date-range-picker') => true
	const isCustomRangePickEnabled = true;
	return LAST_UPDATED_VALUES.filter((value) => {
		if (value === 'custom') {
			return isCustomRangePickEnabled;
		}
		return true;
	});
};

const productKeys1P: ProductKeys1P[] = [
	ProductKeys.Atlas,
	ProductKeys.Confluence,
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
	ProductKeys.Compass,
];

const originalProducts3P: ProductKeys3P[] = [
	ProductKeys.Figma,
	ProductKeys.Github,
	ProductKeys.GoogleDrive,
	ProductKeys.Loom,
	ProductKeys.OneDrive,
	ProductKeys.Slack,
	ProductKeys.Sharepoint,
	ProductKeys.Teams,
	ProductKeys.Dropbox,
];

const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('lastUpdated');

const getProducts3P = () => {
	// fg('rovo-full-page-search-3p-static-config') => false
	return false ? products3PFromConfig : originalProducts3P;
};

export const filter: {
	customValueFields: string[] | undefined;
	getInitialState: (config: CloudConfig) => SelectFilterMonad<'lastUpdated'>;
	id: 'lastUpdated';
	isCompatible: (
		filterState?:
			| {
					id: string;
			  }
			| undefined,
	) => filterState is Readonly<{
		alwaysVisible: boolean;
		customValue?: Record<string, string>;
		customValueFields?: string[];
		didMount: boolean;
		hasSelection: boolean;
		id: 'lastUpdated';
		initialOptions: {
			isLoading: boolean;
			options: SelectFilterOption[];
		};
		lookup: {
			input: string;
			isActive: boolean;
			isDefined: boolean;
			isLoading: boolean;
			lookupResultOptions: SelectFilterOption[];
			options: SelectFilterOption[];
		};
		options: SelectFilterOption[];
		products: ProductKeys[];
		queryParams: {
			isLoading: boolean;
			options: SelectFilterOption[];
		};
		selectedOptions: SelectFilterOption[];
		type: 'single-select' | 'multi-select';
		universal: boolean;
		value: string[];
	}>;
	isSupportCustomValue: boolean;
	products: ProductKeys[];
	queryParams: {
		key: string;
	};
	type: 'single-select';
	universal: boolean;
	withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'lastUpdated'>;
} = createSingleWithCustomOptionSelectFilter({
	id: 'lastUpdated',
	products: [...productKeys1P, ...getProducts3P()],
	alwaysVisible: true,
	universal: true,
	queryParams: {
		key: 'lastModified',
		fetcher: async (_, config) => {
			return getLastUpdatedValue().map((value) => ({
				trackingKey: value,
				label:
					config.intl?.formatMessage(lastUpdatedLabels[value]) ||
					lastUpdatedLabels[value].defaultMessage,
				value,
				queryParamValue: value,
			})) satisfies FetchedSelectFilterOption[];
		},
		customValueParser: (queryParams) => {
			const { to, from } = queryParams;
			const isToDateValid = isIsoDate(to?.[0]);
			const isFromDateValid = isIsoDate(from?.[0]);
			const toDate = isToDateValid && to?.[0] ? to?.[0] : '';
			const fromDate = isFromDateValid && from?.[0] ? from?.[0] : '';
			const invalidDateRange = getInvalidDateRange(fromDate, toDate);
			if (!invalidDateRange) {
				return { to: toDate, from: fromDate };
			}
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return [
				{
					trackingKey: 'anytime',
					label:
						config.intl?.formatMessage(lastUpdatedLabels['anytime']) ||
						lastUpdatedLabels['anytime'].defaultMessage,
					value: 'anytime',
					isDefault: true,
					queryParamValue: LastModified.Anytime,
				},
				...getLastUpdatedValue().map((value) => ({
					trackingKey: value,
					label:
						config.intl?.formatMessage(lastUpdatedLabels[value]) ||
						lastUpdatedLabels[value].defaultMessage,
					value,
					queryParamValue: value,
				})),
			] satisfies FetchedSelectFilterOption[];
		},
	},
	sortCompareFn,
	customValueFields: ['to', 'from'],
});
